import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { PRICE_TIERS as initialTiers } from '../data/mockData';
import apiService from '../services/apiService';

const AppContext = createContext(null);

export function AppExpert({ children }) {
  // Read localStorage synchronously so ProtectedRoute never sees null on first render.
  // Without this, visiting /learner directly always redirects to /auth because
  // the useEffect restoring the session fires *after* the first render.
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('mmx_user');
      return saved ? JSON.parse(saved) : null;
    } catch {
      localStorage.removeItem('mmx_user');
      return null;
    }
  });
  const [toasts, setToasts]       = useState([]);
  const [slots, setSlots]         = useState([]);
  const [bookings, setBookings]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [tags, setTags] = useState([]);
  const [services, setServices] = useState([]);
  const [experts, setExperts] = useState([]);
  const [priceTiers, setPriceTiers] = useState(initialTiers);
  const [selectedCity, setSelectedCity] = useState('');
  const [wallet, setWallet] = useState(null);
  const [courses, setCourses] = useState([]);
  const [expertPacks, setExpertPacks] = useState([]);


  // Load slots & bookings whenever the logged-in user changes
  useEffect(() => {
    if (!user) { setSlots([]); setBookings([]); return; }
    const loadUserData = async () => {
      try {
        if (user.role === 'expert') {
          const [slotsRes, bookingsRes, coursesRes, packsRes] = await Promise.allSettled([
            apiService.getExpertSlots(),
            apiService.getExpertBookings(),
            apiService.getExpertCourses(),
            apiService.getExpertPacks(),
          ]);
          if (slotsRes.status === 'fulfilled' && slotsRes.value.success) setSlots(slotsRes.value.data);
          if (bookingsRes.status === 'fulfilled' && bookingsRes.value.success) setBookings(bookingsRes.value.data);
          if (coursesRes.status === 'fulfilled' && coursesRes.value.success) setCourses(coursesRes.value.data);
          if (packsRes.status === 'fulfilled' && packsRes.value.success) setExpertPacks(packsRes.value.data);
        } else if (user.role === 'learner') {
          const res = await apiService.getLearnerBookings();
          if (res.success) setBookings(res.data);
        }
      } catch (err) {
        console.error('Failed to load user data:', err);
      }
    };
    loadUserData();
  }, [user?.id, user?.role]);

  const [contentLoading, setContentLoading] = useState(true);

  useEffect(() => {
    const loadContent = async () => {
      setContentLoading(true);
      try {
        const [catRes, langRes, tagRes, svcRes, expRes] = await Promise.allSettled([
          apiService.getCategories(),
          apiService.getLanguages(),
          apiService.getTags(),
          apiService.getServices(),
          apiService.getExperts(),
        ]);
        if (catRes.status === 'fulfilled' && catRes.value.success) {
          setCategories(
            catRes.value.data
              .filter(cat => cat.enabled)
              .map(cat => ({
                ...cat,
                id: String(cat.id),
                experts: cat.experts_count || 0,
                subs: (cat.subcategories || []).filter(s => s.enabled).map(s => s.name),
              }))
          );
        }
        if (langRes.status === 'fulfilled' && langRes.value.success) {
          setLanguages(langRes.value.data.filter(l => l.enabled));
        }
        if (tagRes.status === 'fulfilled' && tagRes.value.success) {
          setTags(tagRes.value.data.filter(t => t.enabled));
        }
        if (svcRes.status === 'fulfilled' && svcRes.value.success) {
          setServices(svcRes.value.data);
        }
        if (expRes.status === 'fulfilled' && expRes.value.success) {
          setExperts(expRes.value.data);
        }
      } catch (err) {
        console.error('Failed to load content:', err);
      } finally {
        setContentLoading(false);
      }
    };
    loadContent();
  }, []);

  const toast = useCallback((msg, type = 'default') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);

  const login  = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem('mmx_user', JSON.stringify(userData));
  }, []);
  
  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('mmx_user');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('admin_tab');
    localStorage.removeItem('learner_tab');
  }, []);
  
  const updateUser = useCallback((userData) => {
    setUser(prev => {
      if (prev) {
        const updated = { ...prev, ...userData };
        localStorage.setItem('mmx_user', JSON.stringify(updated));
        return updated;
      }
      return null;
    });
  }, []);

  const addSlot = useCallback(async (slotData) => {
    const payload = {
      date: slotData.date,
      start_time: slotData.start,
      end_time: slotData.end,
      service_title: slotData.service || '',
      price: slotData.price || 0,
      notes: slotData.notes || '',
      type: slotData.type || '1:1',
      min_joinees: slotData.minJoinees || 1,
      max_joinees: slotData.maxJoinees || 10,
    };
    const res = await apiService.createSlot(payload);
    if (res.success) setSlots(prev => [...prev, res.data]);
    return res;
  }, []);

  const deleteSlot = useCallback(async (id) => {
    await apiService.deleteSlot(id);
    setSlots(prev => prev.filter(s => s.id !== id));
  }, []);

  const addBooking = useCallback(async (bookingData) => {
    const res = await apiService.createBooking(bookingData);
    if (res.success) {
      setBookings(prev => [...prev, res.data]);
      setSlots(prev => prev.map(s => {
        if (s.id !== bookingData.slot_id) return s;
        // For group slots, don't mark the slot as fully booked when a learner simply requests/creates a booking
        if (s.type === 'group') return s;
        return { ...s, booked: true };
      }));
    }
    return res;
  }, []);

  const acceptBooking = useCallback(async (id) => {
    const res = await apiService.acceptBooking(id);
    if (res.success) setBookings(prev => prev.map(b => b.id === id ? res.data : b));
  }, []);

  const declineBooking = useCallback(async (id) => {
    const res = await apiService.declineBooking(id);
    if (res.success) {
      setBookings(prev => prev.map(b => b.id === id ? res.data : b));
      setSlots(prev => prev.map(s => {
        if (s.id !== res.data.slot_id) return s;
        // For group slots, don't assume the slot is free after one pending booking is declined
        if (s.type === 'group') return s;
        return { ...s, booked: false };
      }));
    }
  }, []);

  const addBookingToState = useCallback((booking) => {
    setBookings(prev => [...prev, booking]);
    setSlots(prev => prev.map(s => {
      if (s.id !== booking.slot_id) return s;
      if (s.type === 'group') return s;
      return { ...s, booked: true };
    }));
  }, []);

  const loadWallet = useCallback(async () => {
    try {
      const res = await apiService.getWallet();
      if (res.success) {
        // Flatten so wallet.balance / wallet.total_earned / etc. work directly
        setWallet({
          ...res.data.wallet,
          transactions: res.data.transactions,
          thisMonthEarned: res.data.thisMonthEarned,
        });
      }
    } catch (err) {
      console.error('Failed to load wallet:', err);
    }
  }, []);

  const toggleCategory = useCallback((id, val) => {
    setCategories(prev => prev.map(c => c.id === id ? { ...c, enabled: val } : c));
  }, []);

  const addCategory = useCallback((cat) => {
    setCategories(prev => [...prev, cat]);
  }, []);

  const updatePriceTier = useCallback((catId, tier, minmax, val) => {
    setPriceTiers(prev => ({
      ...prev,
      [catId]: { ...prev[catId], [tier]: { ...(prev[catId]?.[tier] || {}), [minmax]: Number(val) } }
    }));
  }, []);

  const addService = useCallback(async (svcData) => {
    const payload = {
      category_id: svcData.catId ? parseInt(svcData.catId) : null,
      title: svcData.title,
      description: svcData.desc || '',
      duration: svcData.duration,
      price: svcData.price,
      subcategories: svcData.subcategories || [],
      tag_ids: svcData.tags || [],
    };
    const res = await apiService.createService(payload);
    if (res.success) {
      // Re-fetch to get the full joined row (category_name, expert_name, etc.)
      const allRes = await apiService.getServices();
      if (allRes.success) setServices(allRes.data);
    }
    return res;
  }, []);

  const updateService = useCallback(async (id, svcData) => {
    const payload = {
      category_id: svcData.catId ? parseInt(svcData.catId) : null,
      title: svcData.title,
      description: svcData.desc || '',
      duration: svcData.duration,
      price: svcData.price,
      subcategories: svcData.subcategories || [],
      tag_ids: svcData.tags || [],
    };
    const res = await apiService.updateService(id, payload);
    if (res.success) {
      const allRes = await apiService.getServices();
      if (allRes.success) setServices(allRes.data);
    }
    return res;
  }, []);

  const removeService = useCallback(async (id) => {
    await apiService.deleteService(id);
    const allRes = await apiService.getServices();
    if (allRes.success) setServices(allRes.data);
  }, []);

  const refreshBookings = useCallback(async () => {
    if (!user) return;
    try {
      if (user.role === 'expert') {
        const res = await apiService.getExpertBookings();
        if (res.success) setBookings(res.data);
      } else if (user.role === 'learner' || user.role === 'receiver') {
        const res = await apiService.getLearnerBookings();
        if (res.success) setBookings(res.data);
      }
    } catch (err) {
      console.error('Failed to refresh bookings:', err);
    }
  }, [user]);

  const refreshSlots = useCallback(async () => {
    if (!user || user.role !== 'expert') return;
    try {
      const res = await apiService.getExpertSlots();
      if (res.success) setSlots(res.data);
    } catch (err) {
      console.error('Failed to refresh slots:', err);
    }
  }, [user]);

  const refreshServices = useCallback(async () => {
    try {
      const res = await apiService.getServices();
      if (res.success) setServices(res.data);
    } catch (err) {
      console.error('Failed to refresh services:', err);
    }
  }, []);

  const refreshExpertPacks = useCallback(async () => {
    try {
      const res = await apiService.getExpertPacks();
      if (res.success) setExpertPacks(res.data);
    } catch (err) {
      console.error('Failed to refresh packs:', err);
    }
  }, []);

  const refreshExperts = useCallback(async () => {
    const res = await apiService.getExperts();
    if (res.success) setExperts(res.data);
  }, []);

  return (
    <AppContext.Provider value={{
      user, login, logout, updateUser,
      toasts, toast,
      slots, addSlot, deleteSlot, refreshSlots,
      bookings, addBooking, acceptBooking, declineBooking, addBookingToState, refreshBookings,
      contentLoading,
      categories, languages, tags, services, experts, toggleCategory, addCategory,
      priceTiers, updatePriceTier,
      addService, updateService, removeService, refreshServices,
      refreshExperts,
      selectedCity, setSelectedCity,
      wallet, loadWallet,
      courses,
      addCourse: (c) => setCourses(prev => [c, ...prev]),
      removeCourse: (id) => setCourses(prev => prev.filter(c => c.id !== id)),
      expertPacks,
      refreshExpertPacks,
      addExpertPack: (p) => setExpertPacks(prev => [p, ...prev]),
      updateExpertPack: (updated) => setExpertPacks(prev => prev.map(p => p.id === updated.id ? updated : p)),
      removeExpertPack: (id) => setExpertPacks(prev => prev.filter(p => p.id !== id)),
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be inside AppExpert');
  return ctx;
}
